import { FulfillmentPositionDisplayMode, FulfillmentProductionState, FulfillmentState } from "@/data/model/Enums";
import type { IFulfillmentGroup } from "@/data/model/FulfillmentGroup";
import type { IOperatorFulfillment } from "@/data/model/Operator";
import { Format } from "@/data/util/Format";

import { config } from "@/operator/Operator";
import { getNameInLocationLanguage } from "@/shared/helpers/ItemNameHelper";
import { trans } from "@/operator/localization/Trans";

export class FulfillmentHelper {
    constructor() {}

    static getTimeLeftToReadyDate(fulfillment: IOperatorFulfillment, date: Date = new Date()) {
        return fulfillment.amendedReadyDateTime.getTime() - date.getTime();
    }

    static getTimeLeftToDate(endDate: Date, date: Date = new Date()) {
        return endDate.getTime() - date.getTime();
    }

    static getFulfillmentProductionState(fulfillment: IOperatorFulfillment): FulfillmentProductionState {
        if (fulfillment.isFulfilled) return FulfillmentProductionState.FulfilledNotPaid;

        if (
            fulfillment.fulfillmentPositionDisplayMode == FulfillmentPositionDisplayMode.Position &&
            fulfillment.state == FulfillmentState.InPreparation
        ) {
            if ((fulfillment.currentQueuePosition as any) <= config.fulfillmentOrangePositionTreshold)
                return FulfillmentProductionState.ReadyShortly;

            return FulfillmentProductionState.InPreparation;
        } else {
            const timeLeft = this.getTimeLeftToReadyDate(fulfillment);
            if (timeLeft <= 0) return FulfillmentProductionState.Ready;

            if (timeLeft <= config.fulfillmentOrangeTimeTreshold) return FulfillmentProductionState.ReadyShortly;

            return FulfillmentProductionState.InPreparation;
        }
    }

    static newestFirstComparator(
        firstModel: IOperatorFulfillment,
        secondModel: IOperatorFulfillment,
        fulfillmentGroups: IFulfillmentGroup[]
    ): number {
        let diff = 0;
        const firstModelState = this.getFulfillmentProductionState(firstModel);
        const secondModelState = this.getFulfillmentProductionState(secondModel);

        // compare states - InPreparation = 0, Ready = 2
        diff = firstModelState - secondModelState;
        if (diff != 0) return diff;

        if (
            firstModelState == FulfillmentProductionState.InPreparation ||
            firstModelState == FulfillmentProductionState.ReadyShortly
        ) {
            // compare displays - Duration = 1, Position = 2
            diff = secondModel.fulfillmentPositionDisplayMode - firstModel.fulfillmentPositionDisplayMode;
            if (diff != 0) return diff;

            if (firstModel.fulfillmentPositionDisplayMode == FulfillmentPositionDisplayMode.Position) {
                // compare scheduled fulfillment date time
                diff = (secondModel.scheduledFulfillmentDateTime.getTime() - firstModel.scheduledFulfillmentDateTime.getTime());
                if (diff != 0) return diff;
                
                // compare positions - 1 - 3 - 3 in front
                diff = (secondModel.currentQueuePosition ?? 0) - (firstModel.currentQueuePosition ?? 0);
                if (diff != 0) return diff;

                // time diff 10:10 - 10:30 -> 10:30 in front
                diff = secondModel.orderDate.getTime() - firstModel.orderDate.getTime();
                if (diff != 0) return diff;

                // compare by fulfillment groups
                diff = -this.compareByFulfillmentGroups(firstModel, secondModel, fulfillmentGroups);
                return diff;
            }

            if (firstModel.fulfillmentPositionDisplayMode == FulfillmentPositionDisplayMode.Duration) {
                // compare scheduled fulfillment date time
                diff = (secondModel.scheduledFulfillmentDateTime.getTime() - firstModel.scheduledFulfillmentDateTime.getTime());
                if (diff != 0) return diff;
            }
        }

        // for all other cases - the newest first
        diff = secondModel.amendedReadyDateTime.getTime() - firstModel.amendedReadyDateTime.getTime(); 
        
        if (diff != 0) return diff;

        // compare by fulfillment groups
        diff = -this.compareByFulfillmentGroups(firstModel, secondModel, fulfillmentGroups);
        return diff;
    }

    static getFulfillmentGroup(fulfillmentGroupId: string | undefined, fulfillmentGroups: IFulfillmentGroup[]) {
        if (fulfillmentGroupId == null || fulfillmentGroups == null) return null;

        return fulfillmentGroups.find((x) => x.id == fulfillmentGroupId);
    }

    static compareByFulfillmentGroups(
        firstModel: IOperatorFulfillment,
        secondModel: IOperatorFulfillment,
        fulfillmentGroups: IFulfillmentGroup[]
    ) {
        const firstFulfillmentGroup = this.getFulfillmentGroup(firstModel.fulfillmentGroupId, fulfillmentGroups);
        const secondFulfillmentGroup = this.getFulfillmentGroup(secondModel.fulfillmentGroupId, fulfillmentGroups);

        return this.compareFulfillmentGroups(firstFulfillmentGroup, secondFulfillmentGroup);
    }

    static compareFulfillmentGroups(
        firstFulfillmentGroup?: IFulfillmentGroup | null,
        secondFulfillmentGroup?: IFulfillmentGroup | null
    ) {
        if (firstFulfillmentGroup == null && secondFulfillmentGroup == null) return 0;

        // move null value after not null value
        if (firstFulfillmentGroup == null) return 1;
        if (secondFulfillmentGroup == null) return -1;

        // move null value after not null value
        if (firstFulfillmentGroup.order == null && secondFulfillmentGroup.order != null) return 1;
        if (secondFulfillmentGroup.order == null && firstFulfillmentGroup.order != null) return -1;

        // get order diff
        const diff = (firstFulfillmentGroup.order ?? 0) - (secondFulfillmentGroup.order ?? 0);
        if (diff != 0) return diff;

        // compare by group names
        const firstFulfillmentGroupName = this.getFulfillmentGroupName(firstFulfillmentGroup);
        const secondFulfillmentGroupName = this.getFulfillmentGroupName(secondFulfillmentGroup);

        return (firstFulfillmentGroupName ?? "").localeCompare(secondFulfillmentGroupName ?? "");
    }

    static getFulfillmentGroupName(fulfillmentGroup?: IFulfillmentGroup) {
        let firstGroupName = trans.production.emptyFulfillmentGroup;
        if (fulfillmentGroup !== undefined) {
            firstGroupName = getNameInLocationLanguage(fulfillmentGroup, true);
        }

        return Format.htmlEscape(firstGroupName);
    }
}
